import { AbstractService } from "./AbstractService";
import { ConfigurableInterface } from "./ConfigurableInterface";
import { createClient } from "webdav/web";
import { FileStat } from "webdav/web/types";
import _ from "lodash";

export interface WebdavServiceInterface extends ConfigurableInterface {
  getVisualHints(params: {
    baseUrl: string;
    kioskId: string;
    expertId: string;
  }): Promise<{
    common: {
      baseUrl: string;
      files: FileStat[];
      favorites?: {
        baseUrl: string;
        files: FileStat[];
      };
    };
    kiosk: {
      baseUrl: string;
      files: FileStat[];
    };
    expert: {
      baseUrl: string;
      files: FileStat[];
      favorites?: {
        baseUrl: string;
        files: FileStat[];
      };
    };
  }>;
}

export class WebdavService extends AbstractService
  implements WebdavServiceInterface {
  public async getVisualHints(params: {
    baseUrl: string;
    kioskId: string;
    expertId: string;
  }): Promise<{
    common: {
      baseUrl: string;
      files: FileStat[];
      favorites?: {
        baseUrl: string;
        files: FileStat[];
      };
    };
    kiosk: {
      baseUrl: string;
      files: FileStat[];
    };
    expert: {
      baseUrl: string;
      files: FileStat[];
      favorites?: {
        baseUrl: string;
        files: FileStat[];
      };
    };
  }> {
    const trimUuid = (uuid: string) => _.trimStart(_.trimEnd(uuid, "}"), "{");
    const baseUrl = _.trimEnd(params.baseUrl, "/");

    const commonHintsBaseUrl = `${baseUrl}/common`,
      expertHintsBaseUrl = `${baseUrl}/expert/${trimUuid(params.expertId)}`,
      kioskHintsBaseUrl = `${baseUrl}/kiosk/${trimUuid(params.kioskId)}`;

    const filterImages = (files: FileStat[]) =>
      _.filter(files, file => _.startsWith(file.mime, "image/"));

    return {
      common: {
        baseUrl: commonHintsBaseUrl,
        files: filterImages(await this.getFiles(commonHintsBaseUrl)),
        favorites: {
          baseUrl: `${commonHintsBaseUrl}/favorites`,
          files: filterImages(
            await this.getFiles(`${commonHintsBaseUrl}/favorites`)
          )
        }
      },
      expert: {
        baseUrl: expertHintsBaseUrl,
        files: filterImages(await this.getFiles(expertHintsBaseUrl)),
        favorites: {
          baseUrl: `${expertHintsBaseUrl}/favorites`,
          files: filterImages(
            await this.getFiles(`${expertHintsBaseUrl}/favorites`)
          )
        }
      },
      kiosk: {
        baseUrl: kioskHintsBaseUrl,
        files: filterImages(await this.getFiles(kioskHintsBaseUrl))
      }
    };
  }

  private async getFiles(url: string): Promise<FileStat[]> {
    try {
      const data = await createClient(url).getDirectoryContents("/");

      return "data" in data ? data.data : data;
    } catch (e) {
      /* empty */
    }

    return [];
  }
}

export class StagingWebdavService extends AbstractService
  implements WebdavServiceInterface {
  public async getVisualHints(params: {
    baseUrl: string;
    kioskId: string;
    expertId: string;
  }): Promise<{
    common: {
      baseUrl: string;
      files: FileStat[];
      favorites?: {
        baseUrl: string;
        files: FileStat[];
      };
    };
    kiosk: {
      baseUrl: string;
      files: FileStat[];
    };
    expert: {
      baseUrl: string;
      files: FileStat[];
      favorites?: {
        baseUrl: string;
        files: FileStat[];
      };
    };
  }> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        const getFile = (i: number) =>
          ({
            filename: `/visualHint${i > 9 ? "" : "0"}${i}.png`,
            basename: `Visual Hint ${i}.png`,
            lastmod: "Thu, 11 Aug 2022 09:12:53 GMT",
            size: 1372767,
            type: "file",
            etag: "4020137258",
            mime: "image/png"
          } as FileStat);

        resolve({
          common: {
            baseUrl: params.baseUrl,
            files: _.map(_.range(1, 2 + 1), i => getFile(i)),
            favorites: {
              baseUrl: params.baseUrl,
              files: _.map(_.range(7, 8 + 1), i => getFile(i))
            }
          },
          kiosk: {
            baseUrl: params.baseUrl,
            files: _.map(_.range(3, 4 + 1), i => getFile(i))
          },
          expert: {
            baseUrl: params.baseUrl,
            files: _.map(_.range(5, 6 + 1), i => getFile(i)),
            favorites: {
              baseUrl: params.baseUrl,
              files: _.map(_.range(9, 10 + 1), i => getFile(i))
            }
          }
        });
      }, this.config.stagingRequestDuration);
    });
  }
}
